"use client"
import { useEffect, useState } from "react"
import {
  Box,
  Button,
  Checkbox,
  Grid,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Card,
} from "@mui/material"
import { useNavigate } from "react-router-dom"
import { StatusChip } from "../../../components/ui"
import {
  FilterBar,
  FilterSelect,
  FilterToggle,
} from "../../../components/ui/FilterBar"
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined"
import GenericTable from "../../../components/table"
import { ProjectApi, UserApi } from "../../../apis"
import WeekPicker from "../../user/phases/weekPicker"
import dayjs from "dayjs"
import { convertDateFormatShortYear } from "../../../utils/helpers"
import { can } from "../../../utils/auth"
import { toast } from "react-toastify"

// Helper to build a real calendar date from the weekly response:
// - entry.day is like "22-Dec"
// - timesheet.weekStart is like "22-12-2025"
const monthShortToNumber = {
  Jan: 1,
  Feb: 2,
  Mar: 3,
  Apr: 4,
  May: 5,
  Jun: 6,
  Jul: 7,
  Aug: 8,
  Sep: 9,
  Oct: 10,
  Nov: 11,
  Dec: 12,
}

const buildISODateFromEntry = (dayStr, weekStartStr) => {
  try {
    if (!dayStr || !weekStartStr) return ""

    // weekStart: "15-12-2025"
    const [, , yearStr] = weekStartStr.split("-")
    const [dayPart, monthShort] = dayStr.split("-")

    const year = Number.parseInt(yearStr, 10)
    const day = Number.parseInt(dayPart, 10)
    const month = monthShortToNumber[monthShort]

    if (!year || !day || !month) return ""

    const date = new Date(year, month - 1, day)
    const yyyy = date.getFullYear()
    const mm = String(date.getMonth() + 1).padStart(2, "0")
    const dd = String(date.getDate()).padStart(2, "0")
    return `${yyyy}-${mm}-${dd}` // ISO-like string that dayjs can parse
  } catch {
    return ""
  }
}

function TimeReport() {
  const weeklyColumns = [
    {
      field: "weekStart",
      headerName: "Week Start",
      width: 120,
      sortable: true,
      renderCell: (params) => <div>{convertDateFormatShortYear(params.value)}</div>,
    },
    {
      field: "weekEnd",
      headerName: "Week End",
      width: 120,
      sortable: true,
      renderCell: (params) => <div>{convertDateFormatShortYear(params.value)}</div>,
    },
    { field: "user_name", headerName: "User", width: 150, sortable: true },
    { field: "totalHours", headerName: "Total Hours", width: 120, sortable: true },
    {
      field: "projectDetails",
      headerName: "Projects & Phases",
      width: 300,
      sortable: false,
      renderCell: (params) => (
        <div
          style={{
            whiteSpace: "pre-line",
            fontWeight: "bold",
            color: "#1976d2",
            fontSize: "14px",
          }}
        >
          {String(params.value || "")
            .split(", ")
            .filter(Boolean)
            .map((project, index) => (
              <div key={index}>• {project}</div>
            ))}
        </div>
      ),
    },
    { field: "status", headerName: "Status", width: 120, sortable: true },
    { field: "notes", headerName: "Notes", width: 200, sortable: true },
  ]

  const projectDailyColumns = [
    {
      field: "date",
      headerName: "Date",
      width: 120,
      sortable: true,
      renderCell: (params) => <div>{dayjs(params.value).isValid() ? dayjs(params.value).format("MM-DD-YYYY") : params.value}</div>,
    },
    {
      field: "project",
      headerName: "Project",
      width: 180,
      sortable: true,
      renderCell: (params) => (
        <div style={{ fontWeight: "bold", color: "#1976d2" }}>{params.value || "—"}</div>
      ),
    },
    { field: "phase", headerName: "Phase", width: 200, sortable: true },
    { field: "user", headerName: "User", width: 180, sortable: true },
    { field: "hours", headerName: "Hours", width: 100, sortable: true },
    { field: "description", headerName: "Description", width: 350, sortable: false },
  ]

  const [projects, setProjects] = useState([])
  const [filter, setFilter] = useState("")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [reportData, setReportData] = useState([])
  const [selectedProject, setSelectedProject] = useState("")
  const [user, setUser] = useState([])
  const [selectedUser, setSelectedUser] = useState("")
  const [selectedDate, setSelectedDate] = useState(dayjs())
  // Status filter: weekly timesheets by status
  const [selectedStatus, setSelectedStatus] = useState("")
  const [statusWeekDate, setStatusWeekDate] = useState(dayjs())
  const [statusTimesheets, setStatusTimesheets] = useState([])
  const [statusLoading, setStatusLoading] = useState(false)
  const [selectedRows, setSelectedRows] = useState(new Set())
  const [actionLoading, setActionLoading] = useState(false)
  const [viewMode, setViewMode] = useState("weekly")
  const navigate = useNavigate()

  const viewOptions = [
    { value: "weekly", label: "Weekly" },
    { value: "daily", label: "Daily" },
  ]

  const filterOptions = [
    { value: "project", label: "Project" },
    { value: "date", label: "Date" },
    { value: "user", label: "User" },
    { value: "status", label: "Status" },
  ]

  const statusFilterOptions = [
    { value: "unsubmitted", label: "Unsubmitted" },
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
  ]

  const handleChange = (event) => {
    setFilter(event.target.value)
    setSelectedProject("")
    setSelectedUser("")
    setSelectedDate(dayjs())
    setSelectedStatus("")
    setStatusWeekDate(dayjs())
    setStatusTimesheets([])
    setSelectedRows(new Set())
    setViewMode("weekly")
  }

  // Aggregate daily rows (from Report by Project) into weekly groups
  const aggregateDailyToWeekly = (dailyRows) => {
    if (!Array.isArray(dailyRows) || dailyRows.length === 0) return []
    const byWeek = {}
    dailyRows.forEach((row) => {
      const d = dayjs(row.date)
      if (!d.isValid()) return
      const weekStart = d.startOf("week").format("DD-MM-YYYY")
      const weekEnd = d.endOf("week").format("DD-MM-YYYY")
      const key = `${weekStart}-${weekEnd}-${row.user || ""}`
      if (!byWeek[key]) {
        byWeek[key] = {
          weekStart,
          weekEnd,
          user_name: row.user || "—",
          totalHours: 0,
          projectDetails: [],
          status: "—",
          notes: "—",
        }
      }
      byWeek[key].totalHours += Number(row.hours) || 0
      const label = [row.project, row.phase].filter(Boolean).join(" - ")
      if (label) byWeek[key].projectDetails.push(label)
    })
    return Object.values(byWeek).map((item) => ({
      ...item,
      projectDetails: Array.isArray(item.projectDetails) ? item.projectDetails.join(", ") || "—" : "—",
      id: `${item.weekStart}-${item.user_name}-${Math.random().toString(36).slice(2)}`,
    }))
  }

  // Detect if reportData is in daily format (has date per row) vs weekly (has weekStart)
  const isReportDataDaily = reportData.length > 0 && reportData[0].date != null && reportData[0].weekStart == null
  const isProjectFilter = filter === "project" && selectedProject
  const hasDailyData = (isProjectFilter || isReportDataDaily) && reportData.length > 0
  const hasWeeklyData = reportData.length > 0 && reportData[0].weekStart != null

  // Expand weekly rows into daily-style rows (one row per week with date = weekStart) so Daily view shows daily columns
  const weeklyToDailyStyle = (weeklyRows) => {
    if (!Array.isArray(weeklyRows) || weeklyRows.length === 0) return []
    return weeklyRows.map((row, index) => {
      const weekStart = row.weekStart || ""
      const dateISO = weekStart ? (() => {
        const parts = weekStart.split("-")
        if (parts.length === 3) {
          const [d, m, y] = parts
          return `${y}-${m}-${d}`
        }
        return weekStart
      })() : ""
      const firstDetail = (row.projectDetails || "").split(", ")[0] || ""
      const detailParts = firstDetail.match(/^(.*?)\s*\((.*?)\)$/)
      const project = detailParts ? detailParts[1] : (firstDetail || "—")
      const phase = detailParts ? detailParts[2] : "—"

      return {
        id: row.id ?? index,
        date: dateISO,
        project,
        phase,
        user: row.user_name ?? row.user ?? "—",
        hours: row.totalHours ?? 0,
        description: row.notes || "—",
      }
    })
  }

  // Compute displayed data and columns for the main report table (not status view)
  const getDisplayDataAndColumns = () => {
    if (filter === "status") return { data: [], columns: weeklyColumns }

    if (viewMode === "daily") {
      if (hasDailyData) {
        return { data: reportData, columns: projectDailyColumns }
      }
      if (hasWeeklyData) {
        const dailyStyleRows = weeklyToDailyStyle(reportData)
        return { data: dailyStyleRows, columns: projectDailyColumns }
      }
      return { data: reportData, columns: projectDailyColumns }
    }

    if (viewMode === "weekly") {
      if (hasDailyData) {
        const aggregated = aggregateDailyToWeekly(reportData)
        return { data: aggregated, columns: weeklyColumns }
      }
      return { data: reportData, columns: weeklyColumns }
    }

    return { data: reportData, columns: isProjectFilter ? projectDailyColumns : weeklyColumns }
  }

  const { data: displayData, columns: displayColumns } = getDisplayDataAndColumns()
  const isDailyViewUnavailable = filter !== "status" && filter !== "" && filter !== "project" && viewMode === "daily"

  const fetchUsers = async () => {
    try {
      const res = await UserApi.getAllUsers()
      const userOptions = res.data.data.users.map((user) => ({
        value: user.id,
        label: user.name,
      }))
      setUser(userOptions)
    } catch (error) {
      console.error("Error fetching users:", error)
      setUser([])
    } finally {
      setLoading(false)
    }
  }

  const fetchProjects = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await ProjectApi.getAllProjects({ status: "active" })
      const projectOptions = res.data.data.projects.map((proj) => ({
        value: proj.name,
        label: proj.name,
      }))
      setProjects(projectOptions)
    } catch (error) {
      console.error("Error fetching projects:", error)
      setError("Failed to load projects. Please try again later.")
      setProjects([])
    } finally {
      setLoading(false)
    }
  }

  const handleProjectChange = (event) => {
    setSelectedProject(event.target.value)
  }

  const handleUserChange = (event) => {
    setSelectedUser(event.target.value)
  }

  const handleDateChange = (date) => {
    setSelectedDate(date)

    // Calculate week start (Monday) and week end (Sunday)
    const weekStart = date.startOf("week").format("DD-MM-YYYY") // Monday
    const weekEnd = date.endOf("week").format("DD-MM-YYYY") // Sunday

    // Update the date range for API call
    fetchReportWithDateRange(weekStart, weekEnd)
  }

  const fetchStatusTimesheets = async (weekStart) => {
    if (!weekStart) return
    setStatusLoading(true)
    setError(null)
    try {
      const response = await UserApi.getAllUserTimeSheetByPayload(weekStart)
      const raw = Array.isArray(response) ? response : response?.data ?? []
      setStatusTimesheets(raw)
    } catch (err) {
      setError(err?.message || "Failed to load weekly timesheets")
      setStatusTimesheets([])
    } finally {
      setStatusLoading(false)
    }
  }

  const handleStatusChange = (event) => {
    setSelectedStatus(event.target.value)
  }

  const handleStatusWeekChange = (date) => {
    setStatusWeekDate(date)
    const weekStart = date.startOf("week").format("DD-MM-YYYY")
    fetchStatusTimesheets(weekStart)
  }

  const filteredStatusTimesheets = selectedStatus
    ? statusTimesheets.filter((row) => {
      const s = (row.status || "").toLowerCase()
      if (selectedStatus === "unsubmitted") return s === "draft" || s === "unsubmitted" || !s
      return s === selectedStatus
    })
    : statusTimesheets

  const weekStartStr = statusWeekDate.startOf("week").format("DD-MM-YYYY")
  const weekEndStr = statusWeekDate.endOf("week").format("DD-MM-YYYY")

  const toggleRow = (id) => {
    setSelectedRows((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAll = () => {
    if (selectedRows.size === filteredStatusTimesheets.length) {
      setSelectedRows(new Set())
    } else {
      setSelectedRows(new Set(filteredStatusTimesheets.map((r) => r.timeSheetId || `${r.id}-${weekStartStr}`)))
    }
  }

  const getRowId = (row) => row.timeSheetId || `${row.id}-${weekStartStr}`

  const handleSubmitSelected = async () => {
    const ids = Array.from(selectedRows)
    if (!ids.length) {
      toast.info("Select at least one timesheet")
      return
    }
    setActionLoading(true)
    try {
      for (const id of ids) {
        const row = filteredStatusTimesheets.find((r) => getRowId(r) === id)
        if (!row) continue
        const userId = row.id
        await UserApi.submitTimeSheet(userId, weekStartStr, weekEndStr)
      }
      toast.success("Timesheet(s) submitted for approval")
      setSelectedRows(new Set())
      fetchStatusTimesheets(weekStartStr)
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to submit")
    } finally {
      setActionLoading(false)
    }
  }

  const handleApproveSelected = async () => {
    const ids = Array.from(selectedRows)
    if (!ids.length) {
      toast.info("Select at least one timesheet")
      return
    }
    setActionLoading(true)
    try {
      for (const id of ids) {
        const row = filteredStatusTimesheets.find((r) => getRowId(r) === id)
        if (!row?.timeSheetId) continue
        await UserApi.approveTimeSheet(row.timeSheetId, "approved")
      }
      toast.success("Timesheet(s) approved")
      setSelectedRows(new Set())
      fetchStatusTimesheets(weekStartStr)
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to approve")
    } finally {
      setActionLoading(false)
    }
  }

  // The drill-down opens an admin screen that can approve and reject, which
  // is beyond what a Manager is meant to do — Managers read the report only.
  // Drilling into a week opens somebody else's timesheet, so it follows the
  // permission that allows reading one rather than the role that used to.
  const canOpenTimesheet = can("timesheets.view_all");

  const handleViewTimesheet = (userId) => {
    navigate(`/admin/weekly-time-sheet/${userId}?date=${weekStartStr}`)
  }

  const fetchReportWithDateRange = async (weekStart, weekEnd) => {
    try {
      setLoading(true)
      const params = {
        weekStart,
        weekEnd,
      }

      if (selectedUser) params.userId = selectedUser
      if (selectedProject) params.projectName = selectedProject
      params.view = viewMode

      const response = await UserApi.getReportByProject({ params })
      const raw = Array.isArray(response.data.data) ? response.data.data : []

      // If project is selected, backend returns per-day rows: [{ date, phase, user, hours, description }]
      if (selectedProject) {
        const normalized = raw.map((row, index) => ({
          id: row.id ?? index,
          ...row,
        }))
        setReportData(normalized)
        return
      }

      // If backend returned daily rows (items have .date), use as-is for Daily view
      if (raw.length > 0 && raw[0].date != null && raw[0].weekStart == null) {
        const normalized = raw.map((row, index) => ({
          id: row.id ?? index,
          ...row,
        }))
        setReportData(normalized)
        return
      }

      // Otherwise keep weekly grouping behavior
      const groupedData = raw.reduce((acc, timesheet) => {
        const key = `${timesheet.weekStart}-${timesheet.weekEnd}-${timesheet.userName}-${timesheet.totalHours}-${timesheet.status || "N/A"}-${timesheet.notes || "N/A"}`

        if (!acc[key]) {
          acc[key] = {
            weekStart: timesheet.weekStart,
            weekEnd: timesheet.weekEnd,
            user_name: timesheet.userName,
            totalHours: timesheet.totalHours,
            status: timesheet.status || "N/A",
            notes: timesheet.notes || "N/A",
            projects: [],
          }
        }

        timesheet.projects.forEach((project) => {
          acc[key].projects.push(`${project.projectName} (${project.phaseName})`)
        })

        return acc
      }, {})

      const formattedData = Object.values(groupedData).map((item) => ({
        ...item,
        projectDetails: item.projects.join(", "),
      }))

      setReportData(formattedData)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const fetchReport = async () => {
    try {
      setLoading(true)
      const params = {}

      if (selectedUser) params.userId = selectedUser
      if (selectedProject) params.projectName = selectedProject
      params.view = viewMode

      if (Object.keys(params).length > 0) {
        const response = await UserApi.getReportByProject({ params })
        const raw = Array.isArray(response.data.data) ? response.data.data : []

        if (selectedProject) {
          const normalized = raw.map((row, index) => ({
            id: row.id ?? index,
            ...row,
          }))
          setReportData(normalized)
          return
        }

        if (raw.length > 0 && raw[0].date != null && raw[0].weekStart == null) {
          const normalized = raw.map((row, index) => ({
            id: row.id ?? index,
            ...row,
          }))
          setReportData(normalized)
          return
        }

        const groupedData = raw.reduce((acc, timesheet) => {
          const key = `${timesheet.weekStart}-${timesheet.weekEnd}-${timesheet.userName}-${timesheet.totalHours}-${timesheet.status || "N/A"}-${timesheet.notes || "N/A"}`

          if (!acc[key]) {
            acc[key] = {
              weekStart: timesheet.weekStart,
              weekEnd: timesheet.weekEnd,
              user_name: timesheet.userName,
              totalHours: timesheet.totalHours,
              status: timesheet.status || "N/A",
              notes: timesheet.notes || "N/A",
              projects: [],
            }
          }

          timesheet.projects.forEach((project) => {
            acc[key].projects.push(`${project.projectName} (${project.phaseName})`)
          })

          return acc
        }, {})

        const formattedData = Object.values(groupedData).map((item) => ({
          ...item,
          projectDetails: item.projects.join(", "),
        }))

        setReportData(formattedData)
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (filter === "project") {
      fetchProjects()
    } else if (filter === "user") {
      fetchUsers()
    }
  }, [filter])

  useEffect(() => {
    if ((selectedUser || selectedProject) && filter !== "date" && filter !== "status") {
      fetchReport()
    }
  }, [selectedUser, selectedProject])

  useEffect(() => {
    if ((selectedUser || selectedProject) && filter !== "date" && filter !== "status") {
      fetchReport()
    }
  }, [viewMode])

  useEffect(() => {
    if (filter === "status") {
      const weekStart = statusWeekDate.startOf("week").format("DD-MM-YYYY")
      fetchStatusTimesheets(weekStart)
    }
  }, [filter])

  // Function to convert JSON data to CSV and trigger download (uses current view)
  const downloadCSV = () => {
    if (!displayData.length) {
      console.log("No report data to download.")
      return
    }

    const headers = displayColumns.map((col) => col.field)
    const csvRows = []
    csvRows.push(headers.join(","))

    displayData.forEach((row) => {
      const values = headers.map((header) => {
        let value = row[header]

        if ((header === "weekStart" || header === "weekEnd") && typeof value === "string") {
          try {
            value = convertDateFormatShortYear(value)
          } catch {
            // fall back to raw
          }
        }

        if (header === "date" && value) {
          const d = dayjs(value)
          if (d.isValid()) {
            value = d.format("MM-DD-YYYY")
          }
        }

        const escaped = ("" + (value ?? "")).replace(/"/g, '""')
        return `"${escaped}"`
      })
      csvRows.push(values.join(","))
    })

    // Combine rows with newlines
    const csvString = csvRows.join("\n")

    // Create a blob and temporary link to trigger download
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.setAttribute("download", "time-report.csv")
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight={600}>
        Time Report
      </Typography>

      {/* Mode first as a segmented control, then only the controls that mode
          actually needs — so the bar never shows an input that does nothing. */}
      <FilterBar sx={{ mt: 3, rowGap: 1.5 }}>
        <FilterToggle
          value={filter}
          onChange={(next) => handleChange({ target: { value: next } })}
          options={filterOptions}
        />

        {filter === "project" && (
          <FilterSelect
            label="Project"
            value={selectedProject}
            onChange={handleProjectChange}
            options={projects}
            allLabel={loading ? "Loading…" : "All projects"}
            minWidth={180}
          />
        )}

        {filter === "date" && (
          <WeekPicker
            selectedDate={selectedDate}
            mb={0}
            onDateChange={handleDateChange}
          />
        )}

        {filter === "user" && (
          <FilterSelect
            label="User"
            value={selectedUser}
            onChange={handleUserChange}
            options={user}
            allLabel={loading ? "Loading…" : "All users"}
            minWidth={180}
          />
        )}

        {filter === "status" && (
          <>
            <FilterSelect
              label="Status"
              value={selectedStatus}
              onChange={handleStatusChange}
              options={statusFilterOptions}
              allLabel="All statuses"
              minWidth={170}
            />
            <WeekPicker
              selectedDate={statusWeekDate}
              mb={0}
              onDateChange={handleStatusWeekChange}
            />
          </>
        )}

        {filter !== "status" && filter !== "" && (
          <FilterToggle
            value={viewMode || "weekly"}
            onChange={(next) => setViewMode(next)}
            options={viewOptions}
          />
        )}

        <Box sx={{ flex: "1 1 0", minWidth: 0 }} />

        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexShrink: 0 }}>
          {filter === "status" ? (
            <>
              <Button
                variant="outlined"
                onClick={handleSubmitSelected}
                disabled={actionLoading || selectedRows.size === 0}
                size="small"
              >
                {actionLoading ? "Processing…" : `Submit${selectedRows.size ? ` (${selectedRows.size})` : ""}`}
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleApproveSelected}
                disabled={actionLoading || selectedRows.size === 0}
                size="small"
              >
                {actionLoading ? "Processing…" : `Approve${selectedRows.size ? ` (${selectedRows.size})` : ""}`}
              </Button>
            </>
          ) : (
            <Button
              variant="outlined"
              onClick={downloadCSV}
              size="small"
              startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
            >
              Export CSV
            </Button>
          )}
        </Box>
      </FilterBar>

      {error && (
        <Box sx={{ mt: 2, color: "error.main" }}>
          <Typography>{error}</Typography>
        </Box>
      )}

      {filter === "status" && (
        <>
          <Box sx={{ mt: 3 }}>
            {statusLoading ? (
              <Typography sx={{ py: 4 }}>Loading weekly timesheets…</Typography>
            ) : (
              <>
              {/* Phones: one card per timesheet. The seven-column table below
                  is unreadable at this width no matter how it scrolls. */}
              <Box sx={{ display: { xs: "flex", md: "none" }, flexDirection: "column", gap: 1.25, mt: 2 }}>
                {filteredStatusTimesheets.length === 0 && !statusLoading ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                    No timesheets for this week{selectedStatus ? ` with status "${selectedStatus}"` : ""}.
                  </Typography>
                ) : (
                  filteredStatusTimesheets.map((row) => {
                    const rowId = getRowId(row)
                    const projectsList = row.rows
                      ? row.rows.flatMap((r) => (r.projects || []).map((p) => p)).filter(Boolean)
                      : []
                    const projectsStr = projectsList.length ? projectsList.join(", ") : "—"
                    return (
                      <Card key={rowId} sx={{ p: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                          <Checkbox
                            checked={selectedRows.has(rowId)}
                            onChange={() => toggleRow(rowId)}
                            sx={{ mt: -1, ml: -1.5 }}
                          />
                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                              {row.user ?? "—"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Week of {convertDateFormatShortYear(weekStartStr)}
                            </Typography>
                          </Box>
                          {row.status ? <StatusChip status={row.status} /> : null}
                        </Box>

                        <Box
                          sx={{
                            mt: 1.5,
                            pt: 1.5,
                            borderTop: 1,
                            borderColor: "border.subtle",
                            display: "grid",
                            gridTemplateColumns: "auto 1fr",
                            rowGap: 1,
                            columnGap: 2,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Total hours
                          </Typography>
                          <Typography variant="body2" sx={{ textAlign: "right", fontWeight: 600 }}>
                            {row.totalHours != null ? Number(row.totalHours).toFixed(2) : "—"}
                          </Typography>

                          <Typography variant="caption" color="text.secondary">
                            Projects
                          </Typography>
                          <Typography variant="body2" sx={{ textAlign: "right" }}>
                            {projectsStr}
                          </Typography>
                        </Box>

                        {canOpenTimesheet && (
                          <Button
                            fullWidth
                            size="small"
                            variant="outlined"
                            sx={{ mt: 2 }}
                            onClick={() => handleViewTimesheet(row.id)}
                          >
                            View timesheet
                          </Button>
                        )}
                      </Card>
                    )
                  })
                )}
              </Box>

              <TableContainer component={Paper} sx={{ mt: 2, display: { xs: "none", md: "block" } }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selectedRows.size > 0 && selectedRows.size < filteredStatusTimesheets.length}
                          checked={filteredStatusTimesheets.length > 0 && selectedRows.size === filteredStatusTimesheets.length}
                          onChange={toggleAll}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>User</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Week start</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Total hours</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Projects</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>View</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredStatusTimesheets.map((row) => {
                      const rowId = getRowId(row)
                      const projectsList = row.rows
                        ? row.rows.flatMap((r) => (r.projects || []).map((p) => p)).filter(Boolean)
                        : []
                      const projectsStr = projectsList.length ? projectsList.join(", ") : "—"
                      return (
                        <TableRow key={rowId}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedRows.has(rowId)}
                              onChange={() => toggleRow(rowId)}
                            />
                          </TableCell>
                          <TableCell>{row.user ?? "—"}</TableCell>
                          <TableCell>{convertDateFormatShortYear(weekStartStr)}</TableCell>
                          <TableCell>{row.totalHours != null ? Number(row.totalHours).toFixed(2) : "—"}</TableCell>
                          <TableCell sx={{ maxWidth: 280 }}>{projectsStr}</TableCell>
                          <TableCell>
                            {row.status ? <StatusChip status={row.status} /> : "—"}
                          </TableCell>
                          <TableCell>
                            {canOpenTimesheet ? (
                              <Link
                                component="button"
                                variant="body2"
                                onClick={() => handleViewTimesheet(row.id)}
                                sx={{ cursor: "pointer" }}
                              >
                                View timesheet
                              </Link>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                    {filteredStatusTimesheets.length === 0 && !statusLoading && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          No timesheets for this week{selectedStatus ? ` with status "${selectedStatus}"` : ""}.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              </>
            )}
          </Box>
        </>
      )}

      {filter !== "status" && (
        <Box sx={{ mt: 3 }}>
          {isDailyViewUnavailable && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Daily view is only available when Report by Project. Showing weekly data below.
            </Typography>
          )}
          <GenericTable
            key={`time-report-${filter}-${viewMode}`}
            data={displayData}
            columns={displayColumns}
            showAction={false}
            loading={loading}
            isSearchShown={false}
          />
        </Box>
      )}
    </Box>
  )
}

export default TimeReport

